import { useState } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo, brandStory, brandValues, milestones } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 - ${siteInfo.fullName}`,
  description: brandStory.subtitle,
  url: `${siteInfo.url}/about`,
  isPartOf: {
    '@type': 'WebSite',
    name: siteInfo.fullName,
    url: siteInfo.url,
  },
  about: {
    '@type': 'Organization',
    name: siteInfo.fullName,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressLocality: '北京',
      addressCountry: 'CN',
    },
    foundingDate: siteInfo.foundedYear,
  },
};

export default function AboutPage() {
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setContactFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormMessage('');

    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      setContactFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const response = await fetch('https://readdy.ai/api/form/d9sr4gl8o1vn02ekukr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        setContactFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查您的网络连接后重试。');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="container-main">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="pb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                关于我们
              </h1>
              <p className="text-sm text-foreground-500">{brandStory.subtitle}</p>
            </div>
          </div>
        </div>

        <section className="py-14 bg-background-50">
          <div className="container-main max-w-4xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16">
              <div>
                <h2 className="text-xl font-bold text-foreground-900 mb-4">
                  {brandStory.title}
                </h2>
                {brandStory.paragraphs.map((p, i) => (
                  <p key={i} className="text-sm text-foreground-600 leading-relaxed mb-3">
                    {p}
                  </p>
                ))}
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20tech%20company%20office%20interior%2C%20creative%20workspace%20with%20warm%20lighting%2C%20team%20collaboration%2C%20minimal%20design%2C%20warm%20orange%20and%20neutral%20tones%2C%20professional%20atmosphere%2C%20clean%20composition&width=800&height=600&seq=qiezi-about-brand&orientation=landscape"
                  alt="茄子视频办公环境"
                  title="茄子视频办公环境"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl font-bold text-foreground-900 text-center mb-10">
                我们的价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {brandValues.map((value, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-xl p-6 border border-background-200/70"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                      <i className={`${value.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl font-bold text-foreground-900 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((m, index) => (
                    <div
                      key={index}
                      className={`relative flex items-start gap-6 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="hidden md:block flex-1"></div>
                      <div className="absolute left-4 md:left-1/2 w-8 h-8 -translate-x-1/2 rounded-full bg-primary-500 border-4 border-background-50 flex items-center justify-center z-10">
                        <span className="w-2 h-2 rounded-full bg-white"></span>
                      </div>
                      <div className="ml-12 md:ml-0 md:flex-1 bg-background-50 rounded-xl p-5 border border-background-200/70">
                        <span className="inline-block px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded mb-2">
                          {m.year}
                        </span>
                        <h3 className="text-base font-semibold text-foreground-900 mb-1">
                          {m.title}
                        </h3>
                        <p className="text-sm text-foreground-600">{m.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-100">
          <div className="container-main max-w-4xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl font-bold text-foreground-900 mb-6">联系我们</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">客服热线</p>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">公司地址</p>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">工作时间</p>
                      <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground-900 mb-6">在线留言</h2>
                <form
                  onSubmit={handleContactSubmit}
                  className="bg-background-50 rounded-xl p-6 border border-background-200/70 space-y-4"
                  data-readdy-form="true"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={contactFormData.name}
                      onChange={handleContactChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={contactFormData.email}
                      onChange={handleContactChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={contactFormData.subject}
                      onChange={handleContactChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={contactFormData.message}
                      onChange={handleContactChange}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900 resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1 text-right">
                      {contactFormData.message.length}/500
                    </p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="spam-protection-field"
                  />

                  {formStatus !== 'idle' && (
                    <div
                      className={`text-sm px-4 py-3 rounded-lg ${
                        formStatus === 'success'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : formStatus === 'error'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-background-100 text-foreground-600'
                      }`}
                    >
                      {formMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}